// src/api/apiClient.js
// Central axios instance for all backend API calls.
// Automatically attaches the MSAL Bearer token to every request.
// Includes forceRelogin for session expiry handling.
// If the token is expired, MSAL silently refreshes it before the call.

import axios from "axios";
import { msalInstance } from "../auth/authProvider";
import { loginRequest } from "../auth/msalConfig";

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL || "http://localhost:3001",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Immediately redirect to M365 login when session expires.
// Clears all MSAL tokens from storage so MSAL init runs fresh on next load.
let _reloginInProgress = false;
const forceRelogin = () => {
  if (_reloginInProgress) return;
  _reloginInProgress = true;
  const clearMsalKeys = (storage) => {
    Object.keys(storage)
      .filter(k => k.startsWith("msal.") || k.includes("login.windows") ||
                   k.includes("accesstoken") || k.includes("idtoken") || k.includes("refreshtoken"))
      .forEach(k => storage.removeItem(k));
  };
  clearMsalKeys(sessionStorage);
  clearMsalKeys(localStorage);
  localStorage.removeItem("msp-os-loaded");
  window.location.href = window.location.origin + "/";
};

// Request interceptor — runs before every API call
// Silently acquires a fresh token and attaches it as Bearer
apiClient.interceptors.request.use(
  async (config) => {
    // Get the active account
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      // No account at all — nothing to refresh, must re-login.
      console.warn("No signed-in account found — redirecting to login");
      forceRelogin();
      return Promise.reject(new Error("No signed-in account found."));
    }

    try {
      // Silently acquire token — uses cached token if still valid,
      // automatically refreshes if expired. No popup or redirect.
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });

      // Attach token to request header
      config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
      return config;

    } catch (err) {
      // On iOS resume, the FIRST silent acquisition can fail while MSAL is still
      // rehydrating its cache from localStorage — even though the refresh token
      // is perfectly valid. Wait briefly and retry once with forceRefresh before
      // giving up, so a transient miss doesn't wipe the session and bounce the
      // user to login. Only forceRelogin if BOTH attempts fail.
      console.warn("Token acquisition failed — retrying once:", err.message);
      try {
        await new Promise((resolve) => setTimeout(resolve, 400));
        const retryResponse = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
          forceRefresh: true,
        });
        config.headers.Authorization = `Bearer ${retryResponse.accessToken}`;
        return config;
      } catch (retryErr) {
        // Both attempts failed — notify the app gracefully.
        // Do NOT call acquireTokenRedirect here: background polling jobs run
        // through this interceptor and a redirect from a background job is what
        // "kicks the user out" mid-session without warning.
        console.warn("Token retry failed — redirecting to login:", retryErr.message);
        forceRelogin();
        return Promise.reject(retryErr);
      }
    }
  },
  (error) => Promise.reject(error)
);

// Response interceptor — handles auth errors globally
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Backend rejected the token — redirect to M365 login.
      console.warn("Backend returned 401 — redirecting to login");
      forceRelogin();
    }
    return Promise.reject(error);
  }
);

export default apiClient;